import { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBars,
  faTimes,
  faSun,
  faMoon,
} from "@fortawesome/free-solid-svg-icons";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { faCode } from "@fortawesome/free-solid-svg-icons";
import { useTheme } from "../context/ThemeContext";

const Navbar = () => {
  const { darkMode, toggleDarkMode } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const navItems = [
    { path: "/", label: "Home" },
    { path: "/about", label: "About" },
    { path: "/projects", label: "Projects" },
    { path: "/contact", label: "Contact" },
    { path: "/my-blogs", label: "Blogs" },
    { path: "/gallery", label: "Gallery" },
  ];

  useEffect(() => {
    document.body.classList.toggle("dark", darkMode);

    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [darkMode]);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const handleNavigation = (path) => {
    setIsOpen(false);
    navigate(path);
  };

  const isActive = (path) => location.pathname === path;

  const logoVariants = {
    hover: {
      scale: 1.05,
      rotate: [0, -5, 5, 0],
      transition: { duration: 0.3 },
    },
  };

  const navItemVariants = {
    hover: {
      scale: 1.1,
      y: -2,
      transition: { type: "spring", stiffness: 300 },
    },
  };

  const mobileMenuVariants = {
    hidden: {
      opacity: 0,
      height: 0,
      transition: {
        duration: 0.3,
        when: "afterChildren",
      },
    },
    visible: {
      opacity: 1,
      height: "auto",
      transition: {
        duration: 0.3,
        when: "beforeChildren",
        staggerChildren: 0.1,
      },
    },
  };

  const mobileItemVariants = {
    hidden: {
      opacity: 0,
      x: -20,
    },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.3 },
    },
  };

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      className={`fixed select-none z-50 top-0 left-0 right-0 transition-all duration-300 ${
        scrolled
          ? `py-2 ${darkMode ? "bg-gray-900/95" : "bg-white/95"} shadow-lg`
          : `py-2 ${darkMode ? "bg-gray-900/80" : "bg-white/80"} shadow-md`
      } backdrop-blur-xl border-b ${
        darkMode ? "border-gray-700/50" : "border-gray-200/50"
      }`}
    >
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center">
          {/* Logo Section */}
          <motion.div
            className="flex items-center gap-1 cursor-pointer group"
            onClick={() => handleNavigation("/")}
            variants={logoVariants}
            whileHover="hover"
          >
            <div
              className={` p-1 rounded transition-all duration-300 ${
                darkMode
                  ? "bg-gradient-to-br from-blue-600 to-purple-600 shadow-lg shadow-blue-500/25"
                  : "bg-gradient-to-br from-blue-500 to-purple-500 shadow-lg shadow-blue-500/25"
              }`}
            >
              <FontAwesomeIcon
                icon={faCode}
                className="text-white text-lg  transition-transform duration-300"
              />
            </div>
            <div>
              <h1
                className={`font-bold uppercase text-xl tracking-wide ${
                  darkMode
                    ? "bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 text-transparent bg-clip-text"
                    : "bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 text-transparent bg-clip-text"
                }`}
              >
                Bhishan  Sah
              </h1>
              <div
                className={`h-0.5 w-0 group-hover:w-full transition-all duration-300 ${
                  darkMode
                    ? "bg-gradient-to-r from-blue-400 to-purple-400"
                    : "bg-gradient-to-r from-blue-600 to-purple-600"
                }`}
              ></div>
            </div>
          </motion.div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-1">
            {navItems.map((item, index) => (
              <motion.button
                key={item.path}
                className={`relative px-4 py-1 rounded-sm font-medium transition-all duration-300 ${
                  isActive(item.path)
                    ? darkMode
                      ? "text-white bg-gradient-to-r from-blue-600 to-purple-600 shadow-lg shadow-blue-500/25"
                      : "text-white bg-gradient-to-r from-blue-500 to-purple-500 shadow-lg shadow-blue-500/25"
                    : darkMode
                    ? "text-gray-300 hover:text-white hover:bg-gray-800/50"
                    : "text-gray-700 hover:text-gray-900 hover:bg-gray-100/50"
                }`}
                onClick={() => handleNavigation(item.path)}
                variants={navItemVariants}
                whileHover="hover"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                {item.label}
                {!isActive(item.path) && (
                  <motion.div
                    className={`absolute bottom-0 left-0 h-0.5 w-0 ${
                      darkMode
                        ? "bg-gradient-to-r from-blue-400 to-purple-400"
                        : "bg-gradient-to-r from-blue-600 to-purple-600"
                    }`}
                    whileHover={{ width: "100%" }}
                    transition={{ duration: 0.3 }}
                  />
                )}
              </motion.button>
            ))}
          </div>

          {/* Right Section */}
          <div className="flex items-center gap-3">
            <div className="relative">
              <button
                onClick={toggleDarkMode}
                className="p-2 rounded-full outline-none"
                aria-label={darkMode ? "Switch to light mode" : "Switch to dark mode"}
                onMouseEnter={() => setShowTooltip(true)}
                onMouseLeave={() => setShowTooltip(false)}
              >
                <FontAwesomeIcon
                  icon={darkMode ? faSun : faMoon}
                  className={darkMode ? "text-yellow-400" : "text-gray-900"}
                />
              </button>

              <AnimatePresence>
                {showTooltip && (
                  <motion.div
                    initial={{ opacity: 0, y: 10, scale: 0.8 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.8 }}
                    className={`absolute top-12 right-0 px-3 py-2 text-xs font-medium rounded-lg shadow-lg ${
                      darkMode ? "bg-gray-800 text-gray-200" : "bg-gray-900 text-white"
                    }`}
                  >
                    {darkMode ? "Light Mode" : "Dark Mode"}
                    <div
                      className={`absolute -top-1 right-3 w-2 h-2 rotate-45 ${
                        darkMode ? "bg-gray-800" : "bg-gray-900"
                      }`}
                    ></div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Mobile Menu Button */}
            <motion.button
              onClick={toggleMenu}
              className={`lg:hidden px-4 py-2 rounded-lg shadow-xl transition-all duration-300 ${
                darkMode
                  ? "bg-gray-800 text-gray-300 hover:bg-gray-700"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            >
              <motion.div
                animate={{ rotate: isOpen ? 180 : 0 }}
                transition={{ duration: 0.3 }}
              >
                <FontAwesomeIcon
                  icon={isOpen ? faTimes : faBars}
                  className="text-sm"
                />
              </motion.div>
            </motion.button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              variants={mobileMenuVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
              className={`lg:hidden mt-4 rounded-2xl overflow-hidden ${
                darkMode
                  ? "bg-gray-800/95 border border-gray-700/50"
                  : "bg-white/95 border border-gray-200/50"
              } backdrop-blur-xl shadow-2xl`}
            >
              <div className="p-4 space-y-2">
                {navItems.map((item) => (
                  <motion.button
                    key={item.path}
                    variants={mobileItemVariants}
                    className={`w-full text-left px-4 py-3 rounded-xl font-medium transition-all duration-300 ${
                      isActive(item.path)
                        ? darkMode
                          ? "text-white bg-gradient-to-r from-blue-600 to-purple-600 shadow-lg"
                          : "text-white bg-gradient-to-r from-blue-500 to-purple-500 shadow-lg"
                        : darkMode
                        ? "text-gray-300 hover:text-white hover:bg-gray-700/50"
                        : "text-gray-700 hover:text-gray-900 hover:bg-gray-100/50"
                    }`}
                    onClick={() => handleNavigation(item.path)}
                    whileHover={{ x: 5 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div className="flex items-center justify-between">
                      {item.label}
                      {isActive(item.path) && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          className="w-2 h-2 rounded-full bg-white"
                        />
                      )}
                    </div>
                  </motion.button>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.nav>
  );
};

export default Navbar;
